import React, { useEffect, useMemo, useState } from "react";
import { slideshowService } from "../../services/slideshowService";

const Slideshow = ({ heightClassName = "h-64" }) => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await slideshowService.getSlides();
        if (!mounted) return;
        setSlides(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Failed to load slideshow:", e);
        if (!mounted) return;
        setSlides([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const activeSlide = useMemo(() => {
    if (!slides || slides.length === 0) return null;
    const idx = Math.min(Math.max(activeIndex, 0), slides.length - 1);
    return slides[idx] || slides[0] || null;
  }, [slides, activeIndex]);

  useEffect(() => {
    if (!slides || slides.length <= 1) return;

    const intervalId = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [slides]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow flex items-center justify-center ${heightClassName}`}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!activeSlide) {
    return null;
  }

  const goPrev = () => setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
  const goNext = () => setActiveIndex((prev) => (prev + 1) % slides.length);

  const imageEl = (
    <img
      src={
        viewportWidth >= 1024
          ? activeSlide.imageUrlLarge || activeSlide.imageUrl || activeSlide.imageUrlMedium || activeSlide.imageUrlSmall
          : viewportWidth >= 768
          ? activeSlide.imageUrlMedium || activeSlide.imageUrl || activeSlide.imageUrlLarge || activeSlide.imageUrlSmall
          : activeSlide.imageUrlSmall || activeSlide.imageUrl || activeSlide.imageUrlMedium || activeSlide.imageUrlLarge
      }
      alt={activeSlide.title || "Slide"}
      className={`w-full object-cover rounded-lg shadow ${heightClassName}`}
    />
  );

  return (
    <div className="relative">
      {activeSlide.linkUrl ? (
        <a href={activeSlide.linkUrl} target="_blank" rel="noreferrer">
          {imageEl}
        </a>
      ) : (
        imageEl
      )}

      {slides.length > 1 && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow"
            aria-label="Previous slide"
          >
            ‹
          </button>
          <button
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow"
            aria-label="Next slide"
          >
            ›
          </button>

          <div className="flex items-center justify-center gap-2 mt-3">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`h-2.5 w-2.5 rounded-full transition-colors ${
                  idx === activeIndex ? "bg-green-600" : "bg-gray-300 hover:bg-gray-400"
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Slideshow;

